import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose, keys, length, prop } from 'ramda'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import {
  getIsAddingMsf,
  getIsShowMSFButton,
  getAddingMsfPanel,
  getDashVehicleSerialNumber,
  getIsRemovingMsf,
  getRemovingMsfPanel,
} from 'selectors/dash'
import { Icon, Tooltip } from 'components/patterns'
import strings from 'utils/strings'
import { setAddMsfFlag, setRemoveMsfFlag, setMsfModalOpen } from 'actions/dash'
import {
  DragIcon,
  ListHeader,
  ListHeaderAndIcon,
  AddMSFButtons,
  CancelMSFButton,
  StyledTooltip,
} from 'components/SwitchAndGauge/DashDrawer/DraggableList/DraggablePartListView.style'

const SectionHeader = ({ definition }) => {
  const dispatch = useDispatch()
  const groupLength = compose(length, keys, prop('groups'))(definition)
  const serialNumber = useSelector(getDashVehicleSerialNumber)

  const { showMSFButton, enableMSFButton, enableAddMsf, disableAddMsfReason } =
    useSelector(getIsShowMSFButton, {
      definition,
      serialNumber,
    })
  const addMsfPanel = useSelector(getAddingMsfPanel)
  const isAddingMsf = useSelector(getIsAddingMsf)
  const isRemovingMsf = useSelector(getIsRemovingMsf)
  const removeMsfPanel = useSelector(getRemovingMsfPanel)
  const label = prop('label', definition)
  const id = prop('id', definition)
  const showHelp = Boolean(groupLength)

  useEffect(
    () => () => {
      dispatch(setAddMsfFlag(false, null))
      dispatch(setRemoveMsfFlag(false, null))
      dispatch(setMsfModalOpen(false, null))
    },
    [dispatch]
  )

  const handleOpenMsfModal = () => {
    dispatch(setMsfModalOpen(true, id, enableAddMsf, disableAddMsfReason))
  }

  const handleCancelMsfMode = () => {
    dispatch(setAddMsfFlag(false, null))
    dispatch(setRemoveMsfFlag(false, null))
  }

  const isInMsfMode =
    (isAddingMsf && addMsfPanel === id) ||
    (isRemovingMsf && removeMsfPanel === id)

  const renderMsfButton = () => {
    if (isInMsfMode) {
      return (
        <CancelMSFButton type="secondary" onClick={handleCancelMsfMode}>
          {strings.cancel}
        </CancelMSFButton>
      )
    }

    if (disableAddMsfReason && !enableMSFButton) {
      return (
        <StyledTooltip
          placement="left"
          title={disableAddMsfReason}
          key={strings.add_remove_msfs}
          trigger={['hover', 'focus']}
        >
          <AddMSFButtons
            compact
            type="secondary"
            disabled={!enableMSFButton}
            aria-label={`${strings.add_remove_msfs}: ${disableAddMsfReason}`}
            onClick={handleOpenMsfModal}
          >
            {strings.add_remove_msfs}
          </AddMSFButtons>
        </StyledTooltip>
      )
    }

    if (enableMSFButton) {
      return (
        <AddMSFButtons compact type="secondary" onClick={handleOpenMsfModal}>
          {strings.add_remove_msfs}
        </AddMSFButtons>
      )
    }

    return null
  }

  return (
    <ListHeaderAndIcon groupLength={groupLength}>
      <ListHeader key={prop('label', definition)}>{label}</ListHeader>
      {showHelp && (
        <DragIcon>
          <Tooltip
            placement="top"
            title={strings.drag_part_to_desired_location}
          >
            <Icon icon="question_mark_tooltip" fill="outlined" size={14} />
          </Tooltip>
        </DragIcon>
      )}
      {showMSFButton && renderMsfButton()}
    </ListHeaderAndIcon>
  )
}

SectionHeader.propTypes = {
  definition: PropTypes.object,
}

export default SectionHeader
